import axios from "axios";
import { message } from "antd";

import { requestHandler } from "./baseApi";
import { environments } from "../constants";
import { AXIOS_GQL_QUERIES } from "../graphql/Queries";
import {
  IAdminLogin,
  IAdminLoginOutput,
  IResendOTP,
  IAssignUserTypeGroupInput,
  IRedeemInviteInput,
  IAddMemberInput,
} from "../../../types";
import { ADMIN_LOGIN_RAW, RESEND_OTP_RAW } from "../graphql/Mutations";

type IUserTypeInput = Record<string, any>;
type IOrgInviteOutput = Record<string, any>;

// FUNC - Create a confirmed user within Cognito User Pool
export const userSignUp = async (mutator, formObject) => {
  // The catch statement on the mutator is used because the try/catch block
  // does not catch the Apollo graphQLErrors when thrown.
  await mutator({
    variables: {
      SignUpInput: {
        email: formObject.email,
        phoneNumber: `+${formObject.phone_number}`,
        gender: formObject.gender,
        firstName: formObject.name,
        lastName: formObject.family_name,
        password: formObject.password,
      },
    },
  }).catch((res) => {
    res.graphQLErrors.forEach((err) => {
      message.error(err.message);
    });
  });
};


// PARTIALLY-DEPRECATED | Kept for legacy reference
// FUNC - Assign user to Cognito Group
export const assign_user_to_usertype_group = async (
  formObject
): Promise<any | null> => {
  try {
    const requestParams = {
      username: formObject.username,
      email: formObject.email,
      groupname: formObject.user_type,
    };

    const resp = await requestHandler({
      method: "POST",
      token: formObject.jwt,
      url_extension: "cognito/users/addtogroup",
      params: requestParams,
    }).catch((err) => {
      message.error("We've encountered an error assigning you to a group");
      console.error("assign_user_to_usertype_group [ERROR]\n", err);
      throw null;
    });

    return resp.message;
  } catch (err) {
    return null;
  }
};

// FUNC - Assign user to Cognito Group v2
export const assign_to_usertype_group = async (
  requestData: IAssignUserTypeGroupInput,
  token: string
): Promise<boolean> => {
  try {
    const response = await axios({
      method: "POST",
      url: `${environments.REACT_APP_BASE_URL}/cognito/users/addtogroup`,
      data: requestData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return true;
  } catch (err) {
    console.error("ASSIGN-TO-USERTYPE-GROUP-ERROR:\n", err);
    return false;
  }
};


// FUNC - Check if organization exists | ORG-V2
export const check_existing_organization_v2 = async (
  formObject
): Promise<any | null> => {
  try {
    const companyName = formObject.org_trading_name;

    const { data } = await axios({
      url: `${environments.REACT_APP_ORG_V2}/companyExists/check`,
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      data: {
        companyName: companyName,
      }
    }).catch((err) => {
      message.error(
        "We've encountered an error checking if your organization exists"
      );
      console.error("check_existing_organization_v2 [ERROR]", err);
      throw null;
    });

    return data;
  } catch (err) {
    return null;
  }
};

// FUNC - Get username
export const get_username = async (
  accessToken: string
): Promise<any | null> => {
  try {
    const graphQLQuery = {
      operationName: "getUsername",
      query: AXIOS_GQL_QUERIES.GET_USERNAME,
      variables: {
        accessToken: accessToken,
      },
    };

    const { data } = await axios({
      url: `${environments.REACT_APP_AUTH_API}`,
      method: "POST",
      data: graphQLQuery,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    const username = data?.data?.getUsername?.username;

    // CHECK - Username Data
    if (!username) {
      throw "Username not found in response";
    }

    return username;
  } catch (err) {
    return null;
  }
};

// FUNC - Create New Organization
export const create_new_user_organization = async (formObject) => {
  try {
    const requestParams = {
      org_name: formObject.org_name,
      org_trading_name: formObject.org_trading_name,
      registration_country: formObject.registration_country,
    };

    const resp = await requestHandler({
      method: "POST",
      token: formObject.jwt,
      url_extension: "organisations",
      params: requestParams,
    }).catch((err) => {
      message.error("We've encountered an error creating your organization");
      console.error("create_new_user_organization [ERROR]\n", err);
      throw null;
    });

    return resp;
  } catch (err) {
    return null;
  }
};

// FUNC - Create New Organization V2
export const create_new_user_organization_v2 = async (
  formObject,
  memberData
) => {
  try {
    const requestParams = {
      org_name: formObject.org_name,
      org_trading_name: formObject.org_trading_name,
      registration_country: formObject.registration_country,
      org_members: memberData,
    };

    const response = await axios({
      method: "POST",
      url: `${environments.REACT_APP_ORG_V2}/`,
      data: requestParams,
      headers: {
        Authorization: `Bearer ${formObject.jwt}`,
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (err) {
    console.error("CREATE-NEW-USER-ORGANIZATION-V2-ERROR:\n", err);
    return null;
  }
};

// FUNC - Get User Details
export const get_all_user_data = async (sub: string): Promise<any | null> => {
  try {
    const res = await requestHandler({
      org_check: true,
      method: "GET",
      url_extension: sub,
    });

    return res;
  } catch (err) {
    message.error("Unable to retrieve user-data");
    return null;
  }
};

// FUNC - Get User Details | ORG-V2
export const get_all_user_data_v2 = async (sub) => {
  try {
    const response = await axios({
      url: `${environments.REACT_APP_ORG_V2}/members/${sub}/organisations`,
      method: "GET",
      headers: {
        Accept: "*/*",
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (err) {
    return null;
  }
};

// FUNC - Update User Details in Ogranization
export const add_org_user_type = async (
  orgData: IUserTypeInput,
  token: string
) => {
  try {
    const response = await axios({
      method: "POST",
      url: `${environments.REACT_APP_ORG_V2}/my-company/addOrgUserType`,
      data: orgData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (err) {
    console.error("ADD-ORG-USER-TYPE-ERROR:\n", err);

    const errorMessage = err?.message;
    message.error(
      errorMessage
        ? errorMessage
        : "We've encountered an issue updating your organisation",
      5
    );

    return null;
  }
};

export const getInvite = async (
  email: string,
  inviteId: string
): Promise<IOrgInviteOutput | null> => {
  try {
    const response = await axios({
      method: "GET",
      url: `${environments.REACT_APP_ORG_V2}/invite/${inviteId}/${email}`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data as IOrgInviteOutput;
  } catch (error) {
    console.error("GET-INVITE-ERROR:\n", error);
    return null;
  }
};

export const redeemInvite = async (
  inviteId: string,
  email: string
): Promise<any | null> => {
  try {
    const response = await axios({
      method: "PUT",
      url: `${environments.REACT_APP_ORG_V2}/invite/${inviteId}/${email.toLowerCase()}`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (error) {
    console.error("REDEEM-INVITE-ERROR:\n", error);
    return null;
  }
};

interface IResendOTPResult {
  successMessage: string | null;
  errorMessage: string | null;
}

export const resendOTP = async ({
  username,
  clientID,
}: IResendOTP): Promise<IResendOTPResult | null> => {
  try {
    const result: IResendOTPResult = {
      successMessage: null,
      errorMessage: null,
    };

    const graphqlMutation = {
      operationName: "ResendOTP",
      query: RESEND_OTP_RAW,
      variables: {
        username: username,
        clientID: clientID,
      },
    };

    const response = await axios({
      method: "POST",
      url: environments.REACT_APP_AUTH_API,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      data: graphqlMutation,
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    const respError = response?.data?.errors?.[0];
    const respMessage = response?.data?.data?.resendOTPConfirmation?.text;

    // VERIFY - Response Data
    if (response?.data?.data?.resendOTPConfirmation?.Error) {
      message.error(response.data.data.resendOTPConfirmation.Error.message);
      return null;
    }

    // CHECK - Response Errors
    if (respError) {
      const errorMessage: string = respError.message;

      // CHECK - Limit Reached
      if (errorMessage.includes("LimitExceededException")) {
        result.errorMessage =
          "You have reached the maximum number of OTP re-send requests. Please try again later or contact support.";
        return result;
      }

      result.errorMessage = errorMessage;
      return result;
    }

    // CHECK - Response Message
    if (!respMessage) {
      throw "No response message found";
    }

    // POPULATE - Success Result
    result.successMessage = respMessage;

    return result;
  } catch (error) {
    console.error("RESEND-OTP-ERROR:\n", error);

    message.error("We've encountered an error with re-sending your OTP", 5);

    return null;
  }
};

export const adminLogin = async ({
  username,
  password,
  clientId,
}: IAdminLogin): Promise<IAdminLoginOutput | null> => {
  try {
    const graphqlMutation = {
      operationName: "adminLogin",
      query: ADMIN_LOGIN_RAW,
      variables: {
        username: username,
        password: password,
        clientId: clientId,
      },
    };

    const response = await axios({
      method: "POST",
      url: environments.REACT_APP_AUTH_API,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      data: graphqlMutation,
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    const result = response?.data?.data?.adminLogin?.AuthenticationResult;

    // VERIFY - Response Data
    if (response?.data?.data?.adminLogin?.AuthenticationResult?.Error) {
      message.error(
        response.data.data.adminLogin.AuthenticationResult.Error.message
      );
      return null;
    }

    // CHECK - Valid Response
    if (!result) {
      throw "adminLogin - Invalid response";
    }

    return result;
  } catch (error) {
    console.error("ADMIN-LOGIN-ERROR:\n", error);

    message.error("We've encountered an error with re-sending your OTP", 5);

    return null;
  }
};

// FUNC - Create New Organization
export const add_invite_member_to_organization = async (
  formObject: IRedeemInviteInput
): Promise<any | null> => {
  try {
    const requestParams = {
      ...formObject.payload,
    };

    const response = await axios({
      method: "POST",
      url: `${environments.REACT_APP_ORG_V2}/${formObject.org_id}/members/${formObject.username}`,
      data: requestParams,
      headers: {
        Authorization: `Bearer ${formObject.jwt}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (err) {
    message.error("Failed to create organisation.");
    return null;
  }
};

export const add_member_to_organisation = async (
  org_role: string,
  first_name: string,
  last_name: string,
  gender: string,
  email: string,
  phoneNumber: string,
  member_id: string,
  org_id: string,
  jwt: string
): Promise<any | null> => {
  try {
    const requestParams = {
      org_role: org_role,
      member_status: "Active",
      statutory_details: {
        gender: gender,
        last_name: last_name,
        first_name: first_name,
      },
      contact_details: {
        personal: {
          email_address: email,
          mobile_number: phoneNumber,
        }
      }
    };
    const response = await axios({
      method: "POST",
      url: `${environments.REACT_APP_ORG_V2}/${org_id}/members/${member_id}`,
      data: requestParams,
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (err) {
    message.error("Failed to create organisation.");
    return null;
  }
};

export const get_driver_details = async (driver_id: string) => {
  try {
    const response = await axios({
      url: `${environments.REACT_APP_ORG_V2}/drivers/${driver_id}/organisations`,
      method: "GET",
      headers: {
        Accept: "*/*",
      },
    });

    // CHECK - Response Data
    if (response.status !== 200) {
      throw response;
    }

    return response.data;
  } catch (err) {
    return null;
  }
};
